import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChangeOrderList } from '../../components/changes/ChangeOrderList';
import { ChangeOrderForm } from '../../components/changes/ChangeOrderForm';
import { ChangeOrderDetail } from '../../components/changes/ChangeOrderDetail';
import { changeOrderService, ChangeOrderWithDetails } from '../../services/change-order.service';
import { useAuth } from '../../contexts/AuthContext';

export function ChangeOrdersPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<ChangeOrderWithDetails[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ChangeOrderWithDetails | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [cumulativeImpact, setCumulativeImpact] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'executed'>('all');

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const [ordersData, impactData] = await Promise.all([
        changeOrderService.getChangeOrders(projectId!),
        changeOrderService.getCumulativeImpact(projectId!)
      ]);
      setOrders(ordersData);
      setCumulativeImpact(impactData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data: any) => {
    try {
      const result = await changeOrderService.createChangeOrder({
        ...data,
        projectId: projectId!
      });
      setShowForm(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al crear orden de cambio');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await changeOrderService.approveChangeOrder(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al aprobar orden');
    }
  };

  const handleReject = async (id: string, reason: string) => {
    try {
      await changeOrderService.rejectChangeOrder(id, reason);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al rechazar orden');
    }
  };

  const handleExecute = async (id: string) => {
    try {
      await changeOrderService.executeChangeOrder(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al ejecutar orden');
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const canApprove = user?.role === 'project_lead' || user?.role === 'general_manager' || user?.role === 'sys_admin';
  const canExecute = user?.role === 'project_lead' || user?.role === 'general_manager' || user?.role === 'sys_admin';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando órdenes de cambio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Órdenes de Cambio</h1>
          <p className="text-gray-600">Gestiona las modificaciones y cambios en obra</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          + Nueva Orden de Cambio
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-md border border-red-200">
          {error}
        </div>
      )}

      {/* Resumen de impacto acumulado */}
      {cumulativeImpact && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Impacto Total</div>
            <div className={`text-2xl font-bold ${cumulativeImpact.totalImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${cumulativeImpact.totalImpact.toFixed(2)}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Aprobadas</div>
            <div className="text-2xl font-bold text-green-600">{cumulativeImpact.approvedCount}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Pendientes</div>
            <div className="text-2xl font-bold text-yellow-600">{cumulativeImpact.pendingCount}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Total Órdenes</div>
            <div className="text-2xl font-bold text-gray-800">{orders.length}</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['all', 'pending', 'approved', 'rejected', 'executed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              filter === status
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'Todos' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Lista de órdenes */}
      <ChangeOrderList
        orders={filteredOrders}
        onSelect={setSelectedOrder}
        onApprove={canApprove ? handleApprove : undefined}
        onReject={canApprove ? handleReject : undefined}
        onExecute={canExecute ? handleExecute : undefined}
      />

      {/* Modal de creación */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Nueva Orden de Cambio</h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <ChangeOrderForm
                projectId={projectId!}
                projectBudget={0} // O el presupuesto real del proyecto
                onSubmit={handleCreate}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalle */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Detalle de Orden de Cambio</h3>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <ChangeOrderDetail
                order={selectedOrder}
                onApprove={canApprove ? handleApprove : undefined}
                onReject={canApprove ? handleReject : undefined}
                onExecute={canExecute ? handleExecute : undefined}
                onClose={() => setSelectedOrder(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}